/**
 * useAuth Hook
 * Authentication state management
 */

import { useState, useEffect, useCallback } from 'react'
import type { UserProfile, LoginRequest, RegisterRequest } from '@shared/types'
import { api } from '../services/api'

interface AuthState {
  user: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  })

  /**
   * Load user from token on mount
   */
  useEffect(() => {
    const loadUser = async () => {
      const token = api.getAccessToken()

      if (!token) {
        setState({ user: null, isAuthenticated: false, isLoading: false })
        return
      }

      try {
        const user = await api.get<UserProfile>('/auth/me')
        setState({ user, isAuthenticated: true, isLoading: false })
      } catch (error) {
        // Token invalid or expired
        api.clearAccessToken()
        setState({ user: null, isAuthenticated: false, isLoading: false })
      }
    }

    loadUser()
  }, [])

  /**
   * Login
   */
  const login = useCallback(async (credentials: LoginRequest) => {
    setState((prev) => ({ ...prev, isLoading: true }))

    try {
      const response = await api.post<{ access_token: string; user: UserProfile }>('/auth/login', credentials)

      api.setAccessToken(response.access_token)
      setState({ user: response.user, isAuthenticated: true, isLoading: false })

      return response.user
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }))
      throw error
    }
  }, [])

  /**
   * Register
   */
  const register = useCallback(async (data: RegisterRequest) => {
    setState((prev) => ({ ...prev, isLoading: true }))

    try {
      const response = await api.post<{ user: UserProfile }>('/auth/register', data)

      setState((prev) => ({ ...prev, isLoading: false }))

      return response.user
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }))
      throw error
    }
  }, [])

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Ignore logout errors
      console.error('Logout error:', error)
    } finally {
      api.clearAccessToken()
      setState({ user: null, isAuthenticated: false, isLoading: false })
    }
  }, [])

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    login,
    register,
    logout,
  }
}
